'use client';

/**
 * Renter Profile — wizard modal.
 *
 * 4-step guided flow the user goes through to share their apartment
 * preferences. Same modal is rendered from both entry points that
 * open a wizard:
 *   - home-page "Personalize your search" pill (screen = 'home')
 *   - post-review success page "Share preferences" button (screen = 'review')
 *
 * Step 1  Basics       — location + max price + bedrooms
 * Step 2  Move-in / Pets
 * Step 3  Amenities
 * Step 4  Commute (work / school / time)
 * Step 5  Success screen with "View matching listings"
 *
 * Save cadence: PATCH the entire dirty step on every Next tap so
 * partial progress survives a drop-off. Step 1 (Basics) is the
 * exception — the embedded ``InstantListingAlertsCard`` auto-saves
 * each field as it's edited, and its own ``useListingAlert`` instance
 * turns alerts on (plus fires ``instant_alerts_on_auto``) the moment
 * the third basic lands. The wizard doesn't enable alerts itself.
 *
 * Styling: hardcoded to the Renter Profile Figma tokens (primary
 * accent #5c5cff, chip-active bg #ecf1ff, etc.) since the app's
 * global tokens don't fully cover the wizard-specific chip states.
 * Kept inline rather than promoting to Tailwind config so the change
 * lands isolated to this feature.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { Spinner } from '@/components/ui';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCheckCircle,
  IconAc,
  IconDishwasher,
  IconDoorman,
  IconElevator,
  IconGym,
  IconLaundryRoom,
  IconParking,
  IconWasherDryer,
} from '@repo/svgs';
import { useListingAlert } from '@/app/saved/hooks/useListingAlert';
import { getListingAlertCache } from '@/app/saved/hooks/listingAlertStore';
import { InstantListingAlertsCard } from '@/features/listing-alert/InstantListingAlertsCard';
import { PreferenceAddressAutocomplete } from '@/components/preferences/PreferenceAddressAutocomplete';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useStore } from '@/providers/StoreProvider';
import { listingsUrl } from '@/utils/routes';
import type { PreferredPets, PreferredCommuteTime } from '@repo/api';
import { cn } from '@/lib/cn';
import {
  getUpcomingMonths,
  calculateExpectedMoveInDates,
  calculateExpectedMoveInSelections,
  type MoveInDateSelection,
} from '@repo/utils';

/** Which entry point opened the modal — flows into every analytics event. */
export type RenterProfileWizardScreen = 'home' | 'review';

interface RenterProfileWizardModalProps {
  open: boolean;
  onClose: () => void;
  screen: RenterProfileWizardScreen;
}

// Wizard step ordering. `basics` is dynamically hidden when the doc
// rule fires (basics already set + listing_alert already on) —
// otherwise the array is used verbatim.
const ALL_STEPS = ['basics', 'move_in_pets', 'amenities', 'commute', 'success'] as const;
type StepKey = (typeof ALL_STEPS)[number];

// Amenity codes reuse the listings amenity filter codes — matched
// against listings on the recommendation side later. Chip icons
// come from the shared @repo/svgs set; the order/label matches the
// Figma spec 1:1.
const AMENITY_OPTIONS: Array<{
  code: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  { code: 'is_unit_air_conditioned', label: 'AC', Icon: IconAc },
  { code: 'is_unit_dishwasher', label: 'Dishwasher', Icon: IconDishwasher },
  { code: 'is_building_doorman', label: 'Doorman', Icon: IconDoorman },
  { code: 'is_building_elevator', label: 'Elevator', Icon: IconElevator },
  { code: 'is_building_gym', label: 'Gym', Icon: IconGym },
  { code: 'is_building_laundry_room', label: 'Laundry room', Icon: IconLaundryRoom },
  { code: 'is_building_parking', label: 'Parking', Icon: IconParking },
  { code: 'is_unit_washer_dryer', label: 'Washer/dryer', Icon: IconWasherDryer },
];

const COMMUTE_TIME_OPTIONS: Array<{ value: PreferredCommuteTime; label: string }> = [
  { value: 'under_15', label: 'Under 15 min' },
  { value: 'under_30', label: 'Under 30 min' },
  { value: 'under_45', label: 'Under 45 min' },
  { value: 'under_60', label: 'Under 60 min' },
];

// -------------------------------------------------------------------
// Shared style tokens (Figma spec) — kept as string constants so
// callsites read like the design and any token drift is a one-line
// edit. Colors are literal hex because the app's global tokens don't
// cover the wizard's active-chip background (#ecf1ff).
// -------------------------------------------------------------------

const chipBase =
  'inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-body-2 text-primary whitespace-nowrap transition-colors';
const chipUnselected = 'bg-primary border border-secondary';
const chipSelected = 'bg-[#ecf1ff] border-2 border-[#5c5cff] px-[15px] py-[11px]'; // -1px pad per side compensates 2px border → same outer size
const inputPill =
  'h-11 w-full rounded-full px-4 border border-secondary bg-primary text-body-2 text-primary placeholder:text-tertiary focus:outline-none focus:border-[#5c5cff]';
const labelText = 'text-body-2-high text-secondary';

export function RenterProfileWizardModal({ open, onClose, screen }: RenterProfileWizardModalProps) {
  const router = useRouter();
  const analytics = useAnalytics();
  const { data, loading, saving, updateListingAlertFields, refetch } = useListingAlert();

  // Doc rule: if listing_alert is already on, the user has already
  // completed the Basics step in a prior session (BE only flips
  // listing_alert=true when neighborhoods + max rent + bedrooms are
  // all populated). Skip the step entirely for this user so we don't
  // ask them to re-enter data they've already provided.
  const computedSteps: StepKey[] = useMemo(() => {
    if (!data) return [...ALL_STEPS];
    if (data.listingAlert) {
      return ALL_STEPS.filter(s => s !== 'basics');
    }
    return [...ALL_STEPS];
  }, [data]);

  // Freeze the step list at the moment the modal opens (once `data`
  // is available). Without this the stepper cell count would visibly
  // jump mid-session: user opens with listing_alert=off (4 cells,
  // Basics is step 1) → completes Basics → BE reconcile flips
  // listing_alert=true → `computedSteps` re-derives without Basics
  // → stepper collapses from 4 → 3 cells while the user is on
  // step 2. The frozen snapshot keeps the same shape for the
  // remainder of the session; a fresh reopen re-freezes.
  const [frozenSteps, setFrozenSteps] = useState<StepKey[] | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      // Clear on close so the next open re-freezes from the
      // then-current data.
      if (frozenSteps !== null) setFrozenSteps(null);
      return;
    }
    // First time we have data while open — snapshot and hold.
    if (data && frozenSteps === null) {
      setFrozenSteps(computedSteps);
    }
  }, [open, data, computedSteps, frozenSteps]);
  const steps = frozenSteps ?? computedSteps;

  const [stepIndex, setStepIndex] = useState(0);
  // `steps` can shrink between renders (e.g. data loading swaps the
  // default 5-step array for the reduced 4-step one before we've had
  // a chance to freeze). Guard against a stale stepIndex pointing
  // past the array.
  const safeStepIndex = Math.min(stepIndex, Math.max(steps.length - 1, 0));
  const currentStep = steps[safeStepIndex];
  const isFirst = safeStepIndex === 0;
  const isSuccess = currentStep === 'success';

  // Reset to step 0 whenever the modal opens. `useLayoutEffect` (not
  // `useEffect`) so the reset runs synchronously BEFORE the browser
  // paints — otherwise reopens flash the prior session's step for
  // one frame (e.g. closed on Amenities, reopened, saw Amenities
  // briefly before it snapped back to Move-in/Pets). This is the
  // "always start from the initial step, don't jump ahead" rule.
  useLayoutEffect(() => {
    if (open) {
      setStepIndex(0);
    }
  }, [open]);

  // Warm up the neighborhoods store the moment the wizard opens so
  // by the time the user hits "View matching listings" on the
  // success screen, the UUID → { name, borough } lookup is ready.
  // Without this pre-fetch the payload builder falls back to
  // dropping the borough/nbr segments (safe, but the user'd land on
  // /listings unfiltered instead of their prefs). No-op if the
  // slice already has data — the fetch dedupes internally.
  const fetchNeighborhoods = useStore(s => s.neighborhoods.fetchNeighborhoods);
  useEffect(() => {
    if (open) void fetchNeighborhoods();
  }, [open, fetchNeighborhoods]);

  // ---------- Local draft state ----------
  //
  // Basics (location + max price + bedrooms) live inside the extracted
  // `InstantListingAlertsCard` — that component reads/writes through
  // its own `useListingAlert` instance directly. The wizard tracks
  // `basicsValid` via `onValidChange` and re-fetches this hook's data
  // after advancing off Basics so downstream steps (URL params on the
  // success screen, the step-skip rule) see the freshest values.

  const [moveIn, setMoveIn] = useState<MoveInDateSelection[]>([]);
  const [pets, setPets] = useState<PreferredPets | null>(null);
  const [petsDetail, setPetsDetail] = useState('');
  const [amenities, setAmenities] = useState<string[]>([]);
  const [work, setWork] = useState('');
  const [school, setSchool] = useState('');
  const [commuteTime, setCommuteTime] = useState<PreferredCommuteTime | null>(null);
  const [basicsError, setBasicsError] = useState(false);
  const [basicsValid, setBasicsValid] = useState(false);
  // Latch set on "View matching listings" click. Kept true until the
  // browser navigates off this page (which unmounts the modal), so
  // the success button shows a spinner during the routing gap.
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    if (!data) return;
    // Move-in is stored as a date range on the BE (mirrors the
    // listings filter shape). Project the range back into chip
    // selections for the UI.
    setMoveIn(
      calculateExpectedMoveInSelections(data.preferredMinMoveInDate, data.preferredMaxMoveInDate),
    );
    setPets(data.preferredPets);
    setPetsDetail(data.preferredPetsDetail ?? '');
    setAmenities(data.preferredAmenities ?? []);
    setWork(data.preferredWorkLocation ?? '');
    setSchool(data.preferredSchoolLocation ?? '');
    setCommuteTime(data.preferredCommuteTime);
  }, [data]);

  const upcomingMonths = useMemo(() => getUpcomingMonths(), []);

  // ---------- Save-per-step helpers ----------

  const saveMoveInPets = useCallback(async () => {
    // Chip selections → date range. Same converter the listings
    // filter uses, so the wizard values slot straight into the
    // filter's own state on the next search.
    const range = calculateExpectedMoveInDates(moveIn);
    await updateListingAlertFields({
      preferredMinMoveInDate: range?.min ?? null,
      preferredMaxMoveInDate: range?.max ?? null,
      preferredPets: pets,
      preferredPetsDetail: pets === 'yes' ? petsDetail : '',
    });
    return true;
  }, [moveIn, pets, petsDetail, updateListingAlertFields]);

  const saveAmenities = useCallback(async () => {
    await updateListingAlertFields({
      preferredAmenities: amenities.length > 0 ? amenities : null,
    });
    return true;
  }, [amenities, updateListingAlertFields]);

  const saveCommute = useCallback(async () => {
    await updateListingAlertFields({
      preferredWorkLocation: work,
      preferredSchoolLocation: school,
      preferredCommuteTime: commuteTime,
    });
    return true;
  }, [work, school, commuteTime, updateListingAlertFields]);

  const onNext = useCallback(async () => {
    if (currentStep === 'basics') {
      if (!basicsValid) {
        setBasicsError(true);
        return;
      }
      // Normally the embedded `InstantListingAlertsCard` handles
      // auto-enable: its own `useListingAlert` instance sees the third
      // basic land, flips `listing_alert`, stamps the one-shot marker,
      // and fires `instant_alerts_on_auto`.
      //
      // But that only happens on a WRITE. A user whose basics were
      // already complete can open the wizard, edit nothing, and tap
      // Next — no field changed, so no PATCH fired and alerts stay
      // off. Catch that case here.
      //
      // Read the shared cache rather than this hook's `data`: the card
      // writes through a different instance, so if its PATCH is still
      // settling, `data` can lag by a render and we'd double-enable.
      //
      // Only `listingAlert` is checked. The one-shot marker gates the
      // passive search-filter automation, not this — the renter
      // profile is a deliberate surface, so reaching the end of Basics
      // with all three fields set should always turn alerts on.
      const current = getListingAlertCache() ?? data;
      const alreadyOn = Boolean(current?.listingAlert);

      if (!alreadyOn) {
        // `basicsValid` is true here (guarded above), so all three
        // fields are populated. The hook stamps the marker for us;
        // we fire the event since it suppresses its own when the
        // caller sets the flag explicitly.
        await updateListingAlertFields({ listingAlert: true });
        analytics.track('instant_alerts_on_auto', { source: screen });
      }

      // Refetch so downstream steps (success-screen URL params, the
      // step-skip rule) read the values the card wrote through its
      // separate hook instance.
      refetch();
      setBasicsError(false);
      setStepIndex(i => Math.min(i + 1, steps.length - 1));
      return;
    }

    let ok = true;
    if (currentStep === 'move_in_pets') ok = await saveMoveInPets();
    else if (currentStep === 'amenities') ok = await saveAmenities();
    else if (currentStep === 'commute') ok = await saveCommute();
    if (!ok) return;
    setStepIndex(i => Math.min(i + 1, steps.length - 1));
  }, [
    currentStep,
    basicsValid,
    data,
    refetch,
    analytics,
    screen,
    updateListingAlertFields,
    saveMoveInPets,
    saveAmenities,
    saveCommute,
    steps.length,
  ]);

  // Save the current step's dirty state without advancing. Used by
  // Previous and the modal-close paths so users don't lose data if
  // they exit a step through anything other than the Next button.
  // Basics has its own auto-save (fires per-field inside the card),
  // so this helper is a no-op for that step.
  const saveCurrentStep = useCallback(async () => {
    if (currentStep === 'move_in_pets') await saveMoveInPets();
    else if (currentStep === 'amenities') await saveAmenities();
    else if (currentStep === 'commute') await saveCommute();
  }, [currentStep, saveMoveInPets, saveAmenities, saveCommute]);

  const onPrev = useCallback(async () => {
    // Persist any pending edits on the current step BEFORE moving
    // back — otherwise the user's info is lost when they return.
    // We deliberately don't gate the step change on the save's
    // success (unlike Next): the user asked to go back, we honor
    // that immediately and let the PATCH complete in the
    // background. Errors will surface on the next Next-tap where
    // it matters for advancement.
    void saveCurrentStep();
    setStepIndex(i => Math.max(i - 1, 0));
  }, [saveCurrentStep]);

  // Latch: the underlying Modal fires BOTH `onOpenChange(false)` and
  // `onClose()` on every dismiss (overlay click, X, Esc), so our
  // wrapper's onClose gets invoked twice — which double-PATCHes the
  // save. Reset on every open so the next dismiss can save again.
  const didHandleCloseRef = useRef(false);
  useEffect(() => {
    if (open) didHandleCloseRef.current = false;
  }, [open]);

  const onModalClose = useCallback(() => {
    if (didHandleCloseRef.current) return;
    didHandleCloseRef.current = true;
    // Same rationale as Previous: persist current step before
    // dismissing so backdrop-clicks / X-taps don't drop data.
    void saveCurrentStep();
    onClose();
  }, [saveCurrentStep, onClose]);

  // Neighborhoods slice — used to translate `listingAlertNeighborhoodIds`
  // (UUIDs stored on the profile) into the `borough:foo|nbr:bar` slug
  // format the /listings route expects. Empty until the neighborhoods
  // fetch has resolved.
  const neighborhoods = useStore(s => s.neighborhoods.neighborhoods) as Array<{
    id: string;
    name: string;
    borough: string;
  }>;
  // Ref mirror of the neighborhoods slice — kept in sync via effect
  // below. `onViewListings` reads through this ref AFTER awaiting the
  // fetch, since the closure's `neighborhoods` variable is captured at
  // callback-creation time and stays stale even once the store
  // updates.
  const neighborhoodsRef = useRef(neighborhoods);
  useEffect(() => {
    neighborhoodsRef.current = neighborhoods;
  }, [neighborhoods]);

  const onViewListings = useCallback(async () => {
    analytics.track('preferences_view_listings', { screen });
    // Race guard for slow networks (bit staging teams on the initial
    // rollout — pre-fetch on modal open hadn't completed by the time
    // the user reached the success screen, so `neighborhoods` was
    // still empty and the URL builder silently dropped the
    // borough/nbr segments, landing the user on unfiltered /listings).
    // If the slice is still empty, block until the fetch resolves.
    // The store call dedupes an in-flight fetch, so this is safe
    // even when the pre-fetch above is already running.
    if (neighborhoods.length === 0) {
      setNavigating(true);
      try {
        await fetchNeighborhoods();
      } catch {
        // Fall through — we'll still build the payload with whatever
        // the store has (possibly empty), same as before. Better to
        // navigate to something than to strand the user.
      }
    }
    // After awaiting, always read from the ref — the closure's
    // `neighborhoods` local is captured at callback-creation time and
    // stays stale even once the store has updated.
    const freshNeighborhoods = neighborhoodsRef.current;
    // No explicit ``markProfileCompleted`` — server reconciles
    // `is_renter_profile_completed` on every save.
    //
    // Build the pipe-separated filter payload the /listings page
    // expects. Format is documented in
    // packages/store/src/slices/listings-filters-slice.ts
    // (`getUrlFilterPayload`). Keys we produce here:
    //   - borough / nbr : from listingAlertNeighborhoodIds (looked
    //     up in the neighborhoods slice — UUID → { name, borough })
    //   - price         : "-MAXRENT" (renter profile has no min-rent)
    //   - bedrooms      : comma-joined ints
    //   - movein        : "MIN-MAX" ISO date range
    //   - amenities     : comma-joined URL slugs (plus `pets` when
    //     preferredPets === 'yes' — pets rides in the amenities
    //     segment per the filter grammar, no separate `pets:` key)
    // Only emit a segment when the underlying value is set.

    const slugify = (s: string): string => s.toLowerCase().replace(/\s+/g, '-');

    const parts: Array<[string, string]> = [];
    const nbhdIds = data?.listingAlertNeighborhoodIds ?? [];
    if (nbhdIds.length > 0 && freshNeighborhoods.length > 0) {
      const byId = new Map(freshNeighborhoods.map(n => [n.id, n]));
      const boroughs = new Set<string>();
      const nbrs: string[] = [];
      for (const id of nbhdIds) {
        const n = byId.get(id);
        if (!n) continue;
        boroughs.add(slugify(n.borough));
        nbrs.push(slugify(n.name));
      }
      if (boroughs.size > 0) parts.push(['borough', Array.from(boroughs).join(',')]);
      if (nbrs.length > 0) parts.push(['nbr', nbrs.join(',')]);
    }

    const maxRent = data?.listingAlertMaxRent ?? null;
    if (typeof maxRent === 'number') {
      // Profile has no min, only max → leave min side blank.
      parts.push(['price', `-${maxRent}`]);
    }

    const bedrooms = data?.listingAlertNumBedrooms ?? [];
    if (bedrooms.length > 0) {
      parts.push([
        'bedrooms',
        bedrooms
          .slice()
          .sort((a, b) => a - b)
          .join(','),
      ]);
    }

    const moveInMin = data?.preferredMinMoveInDate ?? null;
    const moveInMax = data?.preferredMaxMoveInDate ?? null;
    if (moveInMin || moveInMax) {
      parts.push(['movein', `${moveInMin ?? ''}-${moveInMax ?? ''}`]);
    }

    // Profile amenity codes → filter URL slugs. Kept inline (small
    // fixed table) since the grammar is defined in
    // `listings-filters-slice.ts` and expected to change rarely.
    const AMENITY_URL_SLUG: Record<string, string> = {
      is_unit_air_conditioned: 'air-conditioned',
      is_unit_dishwasher: 'dishwasher',
      is_unit_washer_dryer: 'washer-dryer',
      is_building_doorman: 'doorman',
      is_building_elevator: 'elevator',
      is_building_gym: 'gym',
      is_building_laundry_room: 'laundry',
      is_building_parking: 'parking',
    };
    const amenityCodes = data?.preferredAmenities ?? [];
    const amenitySlugs = amenityCodes
      .map(code => AMENITY_URL_SLUG[code])
      .filter((s): s is string => Boolean(s));
    if (data?.preferredPets === 'yes') amenitySlugs.push('pets');
    if (amenitySlugs.length > 0) parts.push(['amenities', amenitySlugs.join(',')]);

    // Build path segment using the shared helper (drops empty parts,
    // joins with `|`, prefixes with `/listings/`).
    const payload = parts.map(([k, v]) => `${k}:${v}`).join('|');
    // Keep the modal open while the /listings navigation is in
    // flight — showing a button spinner. The route transition
    // unmounts the modal naturally once the new page renders, so
    // we don't need an explicit onClose here. Latching `navigating`
    // to true both disables further clicks and swaps the button
    // label for the spinner.
    setNavigating(true);
    router.push(listingsUrl(payload || undefined));
  }, [analytics, screen, data, neighborhoods, router, fetchNeighborhoods]);

  const trackTap = (event: Parameters<typeof analytics.track>[0]) => () => {
    analytics.track(event as any, { screen } as any);
  };

  // Progress bar segments track the ACTIVE `steps` array (minus
  // success). When Basics is skipped (listing_alert already on), the
  // stepper collapses to 3 cells — the doc rule for both home and
  // review entry points is: no basics step, no basics stepper cell.
  const progressSteps: StepKey[] = steps.filter(s => s !== 'success');
  const currentProgressIndex = progressSteps.indexOf(currentStep as StepKey);

  return (
    <ResponsiveModal
      open={open}
      onClose={onModalClose}
      label="Preferences"
      maxWidth="lg"
      // Figma spec: modal shell uses `--radius/rounded-4xl` (32px)
      // instead of the design-system Modal's default `rounded-2xl`
      // (16px). Tailwind's default scale caps at `rounded-3xl` (24px),
      // so we use the arbitrary value to hit the exact spec.
      // `contentClassName` merges into the outer Modal container,
      // and `desktopBodyClassName` keeps the inner body radius in
      // lockstep so we don't get a "double-rim" bottom edge from the
      // two containers having different radii.
      contentClassName="rounded-[32px]"
      desktopBodyClassName="rounded-[32px]"
      // Mobile Figma spec: sheet expands to (near-)full viewport
      // height for every wizard step so the chip grids + amenity
      // list have room without the sheet auto-sizing mid-flow.
      // `mobileFullHeight` forwards to BottomSheet's `fullHeight`
      // which sets the Vaul drawer to 100vh.
      mobileFullHeight
    >
      {/* ResponsiveModal already wraps children in `bg-primary py-4 px-6`
          (see ResponsiveModal.tsx). All internal padding here is
          VERTICAL only — adding another `px-*` layer would double the
          horizontal indent and blow out the modal's spacing (which is
          exactly what happened in v1 of this component).
          `h-full` claims the full-height parent (mobile full-sheet
          mode) to pin the footer to the bottom; on desktop we reset
          to `md:h-auto` so the modal sizes to its content. Same
          reset for `flex-1` on the step body below. */}
      <div className="flex flex-col h-full md:h-auto">
        {/* Progress bar — sits directly under the modal's own header.
            Hidden on the success screen per Figma. */}
        {!isSuccess && (
          <div className="pb-4">
            <div className="flex gap-2">
              {progressSteps.map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'flex-1 min-w-0 h-1 rounded-full',
                    i <= currentProgressIndex ? 'bg-[#5c5cff]' : 'bg-tertiary',
                  )}
                />
              ))}
            </div>
          </div>
        )}

        {/* Step body — no padding wrapper. Inner steps supply their
            own vertical rhythm; ResponsiveModal supplies horizontal.
            Only show the full-body loader on the FIRST load (no
            `data` yet). Refetches after a step save keep `loading`
            true briefly but we already have valid data — swapping
            the whole body for a spinner in that case shrinks the
            modal and looks like a glitch, so we keep the current
            step rendered and rely on the Next button's inline
            spinner (via `saving`) for progress feedback. */}
        {loading && !data ? (
          <div className="py-12 flex items-center justify-center flex-1 md:flex-none">
            <Spinner size="md" />
          </div>
        ) : (
          // Mobile: `flex-1` consumes remaining vertical space so
          // the footer pins to the bottom of the full-height sheet.
          // Desktop: reset via `md:flex-none` — modal sizes to its
          // content and the footer sits right below the last field.
          //
          // Success step is a full-height standalone screen (no
          // wizard footer) — center its content vertically inside
          // the flex-fill container on mobile so the checkmark +
          // copy + CTA sit mid-sheet instead of hugging the top.
          // Desktop uses natural block flow (`md:block`).
          <div
            className={cn(
              'flex-1 md:flex-none',
              isSuccess && 'flex flex-col justify-center md:block',
            )}
          >
            {currentStep === 'basics' && (
              <BasicsStep
                showError={basicsError}
                onValidChange={setBasicsValid}
                onLocationTap={trackTap('preferences_location')}
                onMaxPriceTap={trackTap('preferences_max_price')}
                onBedroomsTap={trackTap('preferences_bedrooms')}
              />
            )}

            {currentStep === 'move_in_pets' && (
              <MoveInPetsStep
                moveIn={moveIn}
                setMoveIn={setMoveIn}
                pets={pets}
                setPets={setPets}
                petsDetail={petsDetail}
                setPetsDetail={setPetsDetail}
                upcomingMonths={upcomingMonths}
                onMoveInTap={trackTap('preferences_move_in')}
                onPetsTap={trackTap('preferences_pets')}
                onPetsDetailTap={trackTap('preferences_pets_details')}
              />
            )}

            {currentStep === 'amenities' && (
              <AmenitiesStep
                amenities={amenities}
                setAmenities={setAmenities}
                onAmenitiesTap={trackTap('preferences_amenities')}
              />
            )}

            {currentStep === 'commute' && (
              <CommuteStep
                work={work}
                setWork={setWork}
                school={school}
                setSchool={setSchool}
                commuteTime={commuteTime}
                setCommuteTime={setCommuteTime}
                onWorkTap={trackTap('preferences_commute_work')}
                onSchoolTap={trackTap('preferences_commute_school')}
                onCommuteTimeTap={trackTap('preferences_commute_time')}
              />
            )}

            {currentStep === 'success' && (
              <SuccessStep onViewListings={onViewListings} saving={saving || navigating} />
            )}
          </div>
        )}

        {/* Footer action bar — hidden on success (CTA is inline)
            and during the FIRST load (spinner-only body). We
            deliberately keep the footer visible during a
            post-step-save refetch, since the step body stays
            rendered too and the modal height should stay stable. */}
        {!isSuccess && !(loading && !data) && (
          <div className="flex items-center justify-between pt-6">
            {!isFirst ? (
              <button
                type="button"
                onClick={onPrev}
                // Matches the Next button's fixed height so both
                // footer buttons stay the same size regardless of
                // saving state.
                className="inline-flex items-center justify-center gap-2 w-32 h-11 px-4 rounded-full bg-primary text-[#5c5cff] text-body-2-high"
                disabled={saving || loading}
              >
                <IconChevronLeft className="w-4 h-4" />
                Previous
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={onNext}
              disabled={saving || loading}
              className={cn(
                // Fixed width + height so the button doesn't jump
                // between "Next + chevron" (bigger content) and
                // just the spinner (smaller content) during saves.
                // h-11 = 44px matches the text-body-2 line-height +
                // py-3 padding of the non-loading state.
                'inline-flex items-center justify-center gap-2 w-32 h-11 px-4 rounded-full text-body-2-high',
                'bg-[#5c5cff] text-inverted disabled:opacity-60',
              )}
            >
              {/* Inline spinner replaces the label + chevron while
                  the step's PATCH is in flight. Prevents double-tap
                  advances and gives the user immediate feedback that
                  the save is running. Reuses the design-system
                  Spinner (currentColor so it inherits the button's
                  white text). */}
              {saving ? (
                <Spinner size="sm" className="text-inverted" />
              ) : (
                <>
                  {currentStep === 'commute' ? 'Done' : 'Next'}
                  {currentStep !== 'commute' && (
                    <IconChevronRight className="w-4 h-4 icon-inverted" />
                  )}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}

// -------------------------------------------------------------------
// Section header — Figma-consistent title + subtitle pattern used
// across every step.
// -------------------------------------------------------------------

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <h2 className="text-heading-6 text-primary">{title}</h2>
      {subtitle && <p className="text-caption text-secondary">{subtitle}</p>}
    </div>
  );
}

// -------------------------------------------------------------------
// Step 1 — Basics
// -------------------------------------------------------------------
// Delegates entirely to the shared `InstantListingAlertsCard` (same
// component /saved renders). `bare` strips the outer border, and
// `showNotificationToggle={false}` hides the "New listing
// notifications" toggle + "Alert preferences" label + Instant alerts
// header — so only the three basics form fields render.
// -------------------------------------------------------------------

function BasicsStep(props: {
  showError: boolean;
  onValidChange: (valid: boolean) => void;
  onLocationTap?: () => void;
  onMaxPriceTap?: () => void;
  onBedroomsTap?: () => void;
}) {
  return (
    // Basics uses wider field-group spacing (`gap-6`) than the other
    // steps — the labels sit visibly above their inputs and the
    // Figma spec breathes more between fields here than in the
    // chip-heavy later steps.
    <div className="flex flex-col gap-6">
      <SectionHeader
        title="What are you looking for?"
        subtitle="We'll use this to find apartments that match your search."
      />
      <InstantListingAlertsCard
        showNotificationToggle={false}
        bare
        stackedBasics
        onValidChange={props.onValidChange}
        onLocationTap={props.onLocationTap}
        onMaxPriceTap={props.onMaxPriceTap}
        onBedroomsTap={props.onBedroomsTap}
        {...(props.showError
          ? {
              showValidationErrors: { location: true, maxPrice: true, bedrooms: true },
            }
          : {})}
      />
    </div>
  );
}

// -------------------------------------------------------------------
// Step 2 — Move-in + Pets
// -------------------------------------------------------------------

function MoveInPetsStep(props: {
  moveIn: MoveInDateSelection[];
  setMoveIn: (v: MoveInDateSelection[]) => void;
  pets: PreferredPets | null;
  setPets: (v: PreferredPets | null) => void;
  petsDetail: string;
  setPetsDetail: (v: string) => void;
  upcomingMonths: Array<{ key: string; label: string }>;
  onMoveInTap: () => void;
  onPetsTap: () => void;
  onPetsDetailTap: () => void;
}) {
  const {
    moveIn,
    setMoveIn,
    pets,
    setPets,
    petsDetail,
    setPetsDetail,
    upcomingMonths,
    onMoveInTap,
    onPetsTap,
    onPetsDetailTap,
  } = props;

  const moveInOptions: Array<{ key: MoveInDateSelection; label: string }> = [
    { key: 'asap' as MoveInDateSelection, label: 'As soon as possible' },
    ...upcomingMonths.map(m => ({ key: m.key, label: m.label })),
  ];

  // Move-in is a RANGE (min + max) — the chips are just a visual
  // sugar layer over that range. Rules for a tap:
  //   • nothing selected yet          → select just the tapped chip
  //   • tapped chip is inside range   → user wants only this one, so
  //                                     collapse selection to it
  //     (edge case: it's the only chip, so tap = deselect all)
  //   • tapped chip is outside range  → extend, filling any gap so
  //                                     the visible selection stays
  //                                     contiguous (min → max)
  const toggleMonth = (key: MoveInDateSelection) => {
    const order = moveInOptions.map(o => o.key);
    const clickedIdx = order.indexOf(key);
    const selectedIdxs = moveIn
      .map(k => order.indexOf(k))
      .filter(i => i >= 0)
      .sort((a, b) => a - b);

    if (selectedIdxs.length === 0) {
      setMoveIn([key]);
      return;
    }

    const minIdx = selectedIdxs[0]!;
    const maxIdx = selectedIdxs[selectedIdxs.length - 1]!;
    const inRange = clickedIdx >= minIdx && clickedIdx <= maxIdx;

    if (inRange) {
      // Only chip: tap toggles off. Otherwise collapse to just this.
      if (minIdx === maxIdx) {
        setMoveIn([]);
      } else {
        setMoveIn([key]);
      }
      return;
    }

    // Outside range: extend to include the tap, filling any gap
    // so the selection stays contiguous (min..max).
    const newMin = Math.min(minIdx, clickedIdx);
    const newMax = Math.max(maxIdx, clickedIdx);
    setMoveIn(order.slice(newMin, newMax + 1));
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <SectionHeader
          title="When do you plan to move-in?"
          subtitle="We'll show apartments that fit your timeline."
        />
        <div
          className="flex flex-wrap gap-2"
          // Only fire when the click actually landed on a chip, not
          // on flex whitespace between chips. Otherwise we'd send a
          // `preferences_move_in` event on any tap in the container.
          onClick={e => {
            if ((e.target as HTMLElement).closest('button')) onMoveInTap();
          }}
        >
          {moveInOptions.map(opt => {
            const selected = moveIn.includes(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => toggleMonth(opt.key)}
                className={cn(chipBase, selected ? chipSelected : chipUnselected)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <SectionHeader
          title="Do you have any pets?"
          subtitle="We'll include apartments that allow pets."
        />
        <div
          className="flex gap-2"
          onClick={e => {
            if ((e.target as HTMLElement).closest('button')) onPetsTap();
          }}
        >
          {(['yes', 'no'] as const).map(v => {
            const selected = pets === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => setPets(selected ? null : v)}
                className={cn(
                  chipBase,
                  'flex-1 capitalize',
                  selected ? chipSelected : chipUnselected,
                )}
              >
                {v}
              </button>
            );
          })}
        </div>
        {pets === 'yes' && (
          <textarea
            className="w-full h-28 rounded-2xl p-4 border border-secondary bg-primary text-body-2 text-primary placeholder:text-tertiary resize-none focus:outline-none focus:border-[#5c5cff]"
            placeholder="Breed, size, age, or anything you would like to share"
            value={petsDetail}
            // `onClick` (not `onFocus`) — see commute Work/School
            // note above. onFocus double-fires during blur→save→
            // refetch and around modal-close focus restoration,
            // which the QA team saw as duplicate `preferences_pets_details`
            // events.
            onClick={onPetsDetailTap}
            onChange={e => setPetsDetail(e.target.value)}
          />
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Step 3 — Amenities
// -------------------------------------------------------------------

function AmenitiesStep(props: {
  amenities: string[];
  setAmenities: (v: string[]) => void;
  onAmenitiesTap: () => void;
}) {
  const { amenities, setAmenities, onAmenitiesTap } = props;
  return (
    <div className="flex flex-col gap-3">
      <SectionHeader
        title="What amenities are must-haves?"
        subtitle="We'll prioritize apartments with your must-have features."
      />
      <div
        className="flex flex-wrap gap-2"
        onClick={e => {
          if ((e.target as HTMLElement).closest('button')) onAmenitiesTap();
        }}
      >
        {AMENITY_OPTIONS.map(a => {
          const selected = amenities.includes(a.code);
          const Icon = a.Icon;
          return (
            <button
              key={a.code}
              type="button"
              onClick={() =>
                setAmenities(
                  selected ? amenities.filter(c => c !== a.code) : [...amenities, a.code],
                )
              }
              className={cn(chipBase, selected ? chipSelected : chipUnselected)}
            >
              <Icon className="w-6 h-6 shrink-0" />
              <span>{a.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Step 4 — Commute
// -------------------------------------------------------------------

function CommuteStep(props: {
  work: string;
  setWork: (v: string) => void;
  school: string;
  setSchool: (v: string) => void;
  commuteTime: PreferredCommuteTime | null;
  setCommuteTime: (v: PreferredCommuteTime | null) => void;
  onWorkTap: () => void;
  onSchoolTap: () => void;
  onCommuteTimeTap: () => void;
}) {
  const {
    work,
    setWork,
    school,
    setSchool,
    commuteTime,
    setCommuteTime,
    onWorkTap,
    onSchoolTap,
    onCommuteTimeTap,
  } = props;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <SectionHeader
          title="Where do you commute to?"
          subtitle="We'll favor apartments with shorter commute times to your locations."
        />
        <div className="flex flex-col gap-1">
          <span className={labelText}>Work</span>
          {/* Selection-only address picker — the value only changes
              when a suggestion is picked (or the box is emptied), so
              `setWork` doubles as the commit handler. The step still
              persists on Next via `saveCommute`. */}
          <PreferenceAddressAutocomplete
            value={work}
            onCommit={setWork}
            className={inputPill}
            placeholder="Search work address"
            aria-label="Work address"
            // `onClick` (not `onFocus`) so we only fire on a real
            // user tap on THIS input. onFocus also fired on
            // programmatic re-focus events during the
            // blur→save→refetch cycle, which surfaced as sibling
            // events cross-firing when the user moved between Work
            // and School.
            onTap={onWorkTap}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className={labelText}>School</span>
          <PreferenceAddressAutocomplete
            value={school}
            onCommit={setSchool}
            className={inputPill}
            placeholder="Search school address"
            aria-label="School address"
            onTap={onSchoolTap}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <SectionHeader title="What's your ideal commute?" />
        <div
          className="grid grid-cols-2 gap-2"
          onClick={e => {
            if ((e.target as HTMLElement).closest('button')) onCommuteTimeTap();
          }}
        >
          {COMMUTE_TIME_OPTIONS.map(opt => {
            const selected = commuteTime === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setCommuteTime(selected ? null : opt.value)}
                className={cn(chipBase, 'w-full', selected ? chipSelected : chipUnselected)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Success screen — replaces the wizard footer with an inline
// full-width CTA per Figma. No Previous button.
// -------------------------------------------------------------------

function SuccessStep({ onViewListings, saving }: { onViewListings: () => void; saving: boolean }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-4 text-center">
        <IconCheckCircle className="w-8 h-8 icon-success" />
        {/* Inner gap-2 (8px) between the heading and its supporting
            copy, per Figma. The outer gap-4 (16px) still separates
            the icon from this text block. */}
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-heading-6 text-primary">Your preferences are saved</h2>
          <p className="text-body-2 text-secondary">
            You can update your preferences any time by visiting your profile.
          </p>
        </div>
      </div>
      <div className="border-t border-tertiary" />
      <div className="flex flex-col items-center gap-2 text-center">
        <h3 className="text-body-1-high text-primary">
          Find apartments that match your preferences
        </h3>
        <p className="text-body-2 text-secondary">
          We&apos;ll show listings based on the preferences you just set.
        </p>
      </div>
      <button
        type="button"
        onClick={onViewListings}
        disabled={saving}
        // Fixed h-14 (56px) matches py-4 + text-body-2 line-height —
        // prevents height collapse when the label is swapped for the
        // spinner while navigating to /listings.
        // Matches the "Schedule a tour" button hover pattern:
        //   default → --Background-background-accent (#5C5CFF)
        //   hover   → --Background-background-accent-hover (#4536F5)
        className="w-full h-14 inline-flex items-center justify-center px-6 rounded-full bg-[#5c5cff] hover:bg-[#4536F5] transition-colors text-inverted text-body-2-high disabled:opacity-60 disabled:hover:bg-[#5c5cff]"
      >
        {saving ? <Spinner size="sm" className="text-inverted" /> : 'View matching listings'}
      </button>
    </div>
  );
}
